// StudentCurriculums.js
import React, { useEffect, useState, useMemo } from "react";
import { getRequest, postRequest, putRequest, deleteRequest } from "../../apiWrapper";
import { toast } from "react-toastify";
import { FaPlus, FaEdit, FaSave, FaTrash, FaTimes } from "react-icons/fa";
import CurriculumCsvUpload from "../../components/report/CurriculumCsvUpload";
import { formatDate, formatDateTimeLocal, toJSTDate } from "../../utils/formatDate";

const COOKIE_NAME = "classora_student_curriculum_tab";

const setCookie = (name, value, days = 30) => {
  const d = new Date();
  d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${encodeURIComponent(value)};expires=${d.toUTCString()};path=/`;
};

const getCookie = (name) => {
  const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  if (match) return decodeURIComponent(match[2]);
  return null;
};

const getQuery = () => new URLSearchParams(window.location.search);
const setQuery = (params) => {
  const url = new URL(window.location.href);
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "") url.searchParams.delete(k);
    else url.searchParams.set(k, v);
  });
  window.history.replaceState({}, "", url.toString());
};

// subjectsマスタから、クエリのsubject値（IDまたは名前）をIDに正規化
const resolveSubjectParamToId = (raw, subjects) => {
  if (!raw) return "";
  if (subjects.some((s) => s.id === raw)) return raw;
  const byName = subjects.find((s) => s.name === raw);
  return byName ? byName.id : "";
};

// 人間が読みやすい日時に変換
const formatReadableDateTime = (iso) => {
  if (!iso) return "";
  const date = new Date(iso); // JSTのまま扱える
  const pad = (n) => String(n).padStart(2, "0");

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} `
       + `${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const StudentCurriculums = () => {
  // ロール判定（管理者=admin）
  const role = sessionStorage.getItem("userRole");
  const isAdmin = role === "admin";

  const [students, setStudents] = useState([]);
  const [subjects, setSubjects] = useState([]); // 名前表示用マスタ
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [curriculums, setCurriculums] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalForm, setModalForm] = useState({
    subjectid: "",
    unit_name: "",
    required_slots: "",
    consumed_slots: "0",
    due_date: "",
  });
  const [activeSubjectId, setActiveSubjectId] = useState("");
  const [showDueDate, setShowDueDate] = useState(false);

  useEffect(() => {
    const fetchInitial = async () => {
      try {
        const stuRes = await getRequest("students?excludeWithdrawn=true");
        const studentList = stuRes.students || [];
        setStudents(studentList);

        const subRes = await getRequest("subjects");
        const subjectList = subRes.subjects || [];
        setSubjects(subjectList);

        const query = getQuery();
        const qStudent = query.get("student");
        const qSubjectRaw = query.get("subject");

        if (qStudent) {
          const found = studentList.find((s) => s.id === qStudent);
          if (found) {
            setSelectedStudent(found);
            const subjectId = resolveSubjectParamToId(qSubjectRaw, subjectList);
            await fetchCurriculums(found.id, subjectId || "");
            setQuery({ student: found.id, subject: subjectId || "" });
            return;
          }
        }

        const cookieVal = getCookie(COOKIE_NAME); // "studentid:subjectid"
        if (cookieVal) {
          const [sid, subid] = cookieVal.split(":");
          const found = studentList.find((s) => s.id === sid);
          if (found) {
            setSelectedStudent(found);
            await fetchCurriculums(found.id, subid || "");
            setQuery({ student: found.id, subject: subid || "" });
            return;
          }
        }
      } catch (err) {
        console.error(err);
        toast.error("初期データの取得に失敗しました");
      }
    };
    fetchInitial();
  }, []);

  // コマ数入力がアクティブになったときに全選択する
  const handleSelectAll = (e) => {
    e.target.select();
  };

  // 科目ごとの「最新カリキュラム」を求める
  const latestBySubject = useMemo(() => {
    const map = {};

    curriculums.forEach((c) => {
      if (!c.subjectid) return;

      const currentTime = new Date(c.updated_at).getTime();
      const existing = map[c.subjectid];

      if (!existing) {
        map[c.subjectid] = c;
      } else {
        const existingTime = new Date(
          existing.updated_at
        ).getTime();
        if (currentTime > existingTime) {
          map[c.subjectid] = c;
        }
      }
    });

    return map; // { [subjectid]: 最新カリキュラム }
  }, [curriculums]);

  // 今選択中の科目の「最新カリキュラム」
  const latestCurriculumForActiveSubject = useMemo(() => {
    if (!activeSubjectId) return null;
    return latestBySubject[activeSubjectId] || null;
  }, [latestBySubject, activeSubjectId]);


  const saveTabToCookie = (studentid, subjectid = "") => {
    if (!studentid) return;
    setCookie(COOKIE_NAME, `${studentid}:${subjectid}`);
  };

  // preferredSubjectId が存在していればそれをタブに保持、なければ先頭科目
  const fetchCurriculums = async (studentid, preferredSubjectId = "") => {
    try {
      const res = await getRequest(`student_curriculums?studentid=${studentid}`);
      const rows = res.curriculums || [];
      setCurriculums(rows);

      const subjectIds = rows.map((r) => r.subjectid);
      const uniqueSubjectIds = Array.from(new Set(subjectIds));

      if (uniqueSubjectIds.length === 0) {
        setActiveSubjectId("");
        setQuery({ student: studentid, subject: "" });
        return;
      }

      if (preferredSubjectId && uniqueSubjectIds.includes(preferredSubjectId)) {
        setActiveSubjectId(preferredSubjectId);
        saveTabToCookie(studentid, preferredSubjectId);
        setQuery({ student: studentid, subject: preferredSubjectId });
        return;
      }

      const cookieVal = getCookie(COOKIE_NAME);
      if (cookieVal) {
        const [sid, subid] = cookieVal.split(":");
        if (sid === studentid && uniqueSubjectIds.includes(subid)) {
          setActiveSubjectId(subid);
          setQuery({ student: studentid, subject: subid });
          return;
        }
      }

      const first = uniqueSubjectIds[0];
      setActiveSubjectId(first);
      saveTabToCookie(studentid, first);
      setQuery({ student: studentid, subject: first });
    } catch (err) {
      console.error(err);
      toast.error("カリキュラムの取得に失敗しました");
      setCurriculums([]);
      setActiveSubjectId("");
      setQuery({ student: studentid, subject: "" });
    }
  };

  const handleStudentSelect = async (e) => {
    const studentid = e.target.value;
    const stu = students.find((s) => s.id === studentid) || null;
    setSelectedStudent(stu);
    setCurriculums([]);
    setEditingId(null);

    if (stu) {
      saveTabToCookie(stu.id, "");
      setQuery({ student: stu.id, subject: "" });
      await fetchCurriculums(stu.id);
    } else {
      setActiveSubjectId("");
      setQuery({ student: "", subject: "" });
    }
  };

  const handleEdit = (row) => {
    setEditingId(row.id);
    setEditForm({
      subjectid: row.subjectid,
      unit_name: row.unit_name,
      required_slots: row.required_slots,
      consumed_slots: row.consumed_slots,
      due_date: row.due_date ? formatDate(row.due_date) : "",
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleUpdate = async (id) => {
    try {
      const payload = {
        subjectid: editForm.subjectid,
        unit_name: editForm.unit_name,
        // required_slotsは管理者以外は編集不可（UIで無効化）。念のため値は送るが、RLS側の許可/拒否に従う
        required_slots: Number(editForm.required_slots),
        consumed_slots: Number(editForm.consumed_slots),
        due_date: editForm.due_date || null,
      };
      const res = await putRequest(`student_curriculums/${id}`, payload);
      if (!res.ok) {
        const data = await res.json();
        toast.error(data.error || "更新に失敗しました");
        return;
      }
      toast.success("更新しました");
      if (selectedStudent) await fetchCurriculums(selectedStudent.id, activeSubjectId);
    } catch (err) {
      console.error(err);
      toast.error("更新に失敗しました");
    } finally {
      setEditingId(null);
      setEditForm({});
    }
  };

  const handleDelete = async (id) => {
    if (!isAdmin) return; // 管理者以外は削除不可
    if (!window.confirm("本当に削除しますか？")) return;
    try {
      const res = await deleteRequest(`student_curriculums/${id}`);
      if (!res.ok) {
        const data = await res.json();
        toast.error(data.error || "削除に失敗しました");
        return;
      }
      toast.success("削除しました");
      if (selectedStudent) await fetchCurriculums(selectedStudent.id, activeSubjectId);
    } catch (err) {
      console.error(err);
      toast.error("削除に失敗しました");
    }
  };

  const openModal = () => {
    setModalForm({
      subjectid: activeSubjectId || "",
      unit_name: "",
      required_slots: "",
      consumed_slots: "0",
      due_date: "",
    });
    setIsModalOpen(true);
  };

  const closeModal = () => setIsModalOpen(false);

  const handleModalChange = (e) => {
    const { name, value } = e.target;
    setModalForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreate = async () => {
    if (!selectedStudent) {
      toast.error("生徒を選択してください");
      return;
    }
    if (!modalForm.subjectid || !modalForm.unit_name || modalForm.required_slots === "") {
      toast.error("必須項目を入力してください");
      return;
    }

    const payload = {
      studentid: selectedStudent.id,
      items: [
        {
          subjectid: modalForm.subjectid,
          unit_name: modalForm.unit_name,
          required_slots:
            modalForm.required_slots === "" || modalForm.required_slots == null
              ? 0
              : Number(modalForm.required_slots),
          consumed_slots: modalForm.consumed_slots ? Number(modalForm.consumed_slots) : 0,
          due_date: modalForm.due_date || null,
        },
      ],
    };

    try {
      const res = await postRequest("student_curriculums", payload);
      if (res && res.error) {
        toast.error(res.error || "登録に失敗しました");
        return;
      }
      toast.success("登録しました");
      closeModal();
      if (selectedStudent) await fetchCurriculums(selectedStudent.id, modalForm.subjectid);
    } catch (err) {
      console.error(err);
      toast.error("登録に失敗しました");
    }
  };

  // CSV取り込み（管理者のみ）
  const handleCsvImported = async (rows) => {
    if (!isAdmin) {
      toast.error("CSVインポートは管理者のみ利用できます。");
      return;
    }
    if (!selectedStudent) {
      toast.error("先に生徒を選択してください");
      return;
    }

    const byName = new Map(subjects.map((s) => [s.name, s.id]));
    const byId = new Set(subjects.map((s) => s.id));

    const items = [];
    const errors = [];

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];

      const rawSubject = String(row.subject ?? "").trim();
      const subjectid = byId.has(rawSubject) ? rawSubject : byName.get(rawSubject) || null;

      const unit_name = String(row.unitName ?? "").trim();
      const required =
        row.requiredSessions === "" || row.requiredSessions == null
          ? 0
          : Number(row.requiredSessions);
      const due_date = row.deadline ? String(row.deadline).trim() : null;

      if (!subjectid || !unit_name || !Number.isFinite(required) || required < 0) {
        errors.push({ index: i + 1, subject: row.subject, unit: unit_name });
        continue;
      }

      items.push({
        subjectid,
        unit_name,
        required_slots: required,
        consumed_slots: 0,
        due_date: due_date || null,
      });
    }

    if (items.length === 0) {
      toast.error("有効な行がありません。科目名/ID・単元名・必要コマ数を確認してください。");
      return;
    }

    const payload = {
      studentid: selectedStudent.id,
      items,
    };

    try {
      const res = await postRequest("student_curriculums", payload);
      if (res && res.error) {
        toast.error(res.error || "登録に失敗しました");
        return;
      }

      if (errors.length > 0) {
        toast.warn(`${errors.length}行スキップしました（科目や必須項目の不足）。`);
        console.warn("スキップ行:", errors);
      }

      toast.success(`${items.length}件登録しました`);
      if (selectedStudent) await fetchCurriculums(selectedStudent.id, activeSubjectId);
    } catch (err) {
      console.error(err);
      toast.error("登録に失敗しました");
    }
  };

  // この生徒が持っている科目タブ（カリキュラムから抽出）
  const subjectTabs = (() => {
    const map = new Map();
    for (const c of curriculums) {
      if (!map.has(c.subjectid)) {
        const sub = subjects.find((s) => s.id === c.subjectid);
        map.set(c.subjectid, sub ? sub.name : c.subjectid);
      }
    }
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  })();

  const filteredCurriculums = curriculums.filter(
    (c) => !activeSubjectId || c.subjectid === activeSubjectId
  );

  const handleTabClick = (subjectid) => {
    setActiveSubjectId(subjectid);
    if (selectedStudent) {
      saveTabToCookie(selectedStudent.id, subjectid);
      setQuery({ student: selectedStudent.id, subject: subjectid });
    }
  };

  return (
    <div className="p-4 pt-20">
      <h1 className="text-2xl font-bold mb-4">生徒カリキュラム管理</h1>

      {isAdmin && <CurriculumCsvUpload onImported={handleCsvImported} />}

      <div className="mb-4 flex items-center gap-4">
        <select
          className="p-2 border rounded"
          onChange={handleStudentSelect}
          value={selectedStudent?.id || ""}
        >
          <option value="">生徒を選択</option>
          {students.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>

        <button
          onClick={openModal}
          disabled={!selectedStudent}
          className={`flex items-center gap-2 px-3 py-2 rounded ${selectedStudent ? "bg-blue-600 text-white hover:bg-blue-700" : "bg-gray-300 text-gray-500"
            }`}
        >
          <FaPlus /> 追加
        </button>
      </div>

      {selectedStudent && subjectTabs.length > 0 && (
        <div className="mb-4 flex flex-wrap gap-2">
          {subjectTabs.map((tab) => {
            const isActive = tab.id === activeSubjectId;
            return (
              <button
                key={tab.id}
                onClick={() => handleTabClick(tab.id)}
                className={
                  "px-3 py-1 rounded border text-sm " +
                  (isActive
                    ? "bg-blue-600 text-white border-blue-600"
                    : "bg-white text-gray-700 hover:bg-gray-100")
                }
              >
                {tab.name}
              </button>
            );
          })}
        </div>
      )}

      {selectedStudent ? (
        <div className="bg-white rounded shadow p-4">
          {subjectTabs.length === 0 ? (
            <p className="text-gray-500">この生徒のカリキュラムはありません</p>
          ) : filteredCurriculums.length === 0 ? (
            <p className="text-gray-500">この科目のカリキュラムはありません</p>
          ) : (
            <>

              {/* 全カリキュラムの最終更新情報 */}
              {latestCurriculumForActiveSubject && (
                <div className="mb-2 text-xs md:text-sm text-gray-600">
                  この科目の最終更新：{" "}
                  {formatReadableDateTime(
                    latestCurriculumForActiveSubject.updated_at
                  )}
                  <span className="ml-2">
                    （単元: {latestCurriculumForActiveSubject.unit_name}）
                  </span>
                </div>
              )}


              {/* 期限列の表示トグル */}
              <div className="flex justify-end mb-2">
                <label className="inline-flex items-center gap-1 text-xs md:text-sm">
                  <input
                    type="checkbox"
                    checked={showDueDate}
                    onChange={(e) => setShowDueDate(e.target.checked)}
                    className="mr-1"
                  />
                  期限列を表示
                </label>
              </div>

              {/* テーブルを横スクロール可能に */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm border min-w-[480px]">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="p-2 border align-top break-words min-w-[8rem] md:min-w-[12rem]">
                        単元名
                      </th>
                      <th className="p-2 border w-20 md:w-24 text-center">
                        <span className="hidden md:inline">必要コマ数</span>
                        <span className="md:hidden">必要</span>
                      </th>
                      <th className="p-2 border w-20 md:w-24 text-center">
                        <span className="hidden md:inline">消化コマ数</span>
                        <span className="md:hidden">消化</span>
                      </th>
                      {/* 期限列はオプション表示 */}
                      {showDueDate && (
                        <th className="p-2 border w-24 md:w-32 text-center whitespace-nowrap">
                          期限
                        </th>
                      )}
                      <th className="p-2 border w-20 md:w-28 text-center">操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCurriculums.map((c) => {
                      const isEditing = editingId === c.id;

                      const fullyDone = c.consumed_slots >= c.required_slots;
                      const isOverdue =
                        !fullyDone && c.due_date && new Date(c.due_date) < new Date();

                      // ▼ この行が「自分の科目で最新か？」を判定
                      const latestForThisSubject = latestBySubject[c.subjectid];
                      const isLatestInSubject =
                        latestForThisSubject && latestForThisSubject.id === c.id;

                      // まずは元のルールで背景色を決める
                      let rowBg = fullyDone
                        ? "bg-green-100"
                        : isOverdue
                          ? "bg-red-50"
                          : "bg-white";

                      // ▼ 科目内の最新なら背景色を上書き
                      if (isLatestInSubject) {
                        rowBg = "bg-blue-100"; // 好きな色に調整OK
                      }

                      return (
                        <tr key={c.id} className={`${rowBg} border-b`}>
                          {/* 単元名：折り返して表示、少しでも広く */}
                          <td className="p-2 border align-top break-words">
                            {isEditing ? (
                              <input
                                name="unit_name"
                                value={editForm.unit_name}
                                onChange={handleEditChange}
                                className="border rounded p-1 w-full"
                              />
                            ) : (
                              c.unit_name
                            )}
                          </td>

                          {/* 必要コマ数 */}
                          <td className="p-2 border text-center">
                            {isEditing ? (
                              <input
                                type="number"
                                name="required_slots"
                                value={editForm.required_slots}
                                onChange={handleEditChange}
                                onFocus={handleSelectAll}
                                className={`border rounded p-1 w-full ${!isAdmin ? "bg-gray-100 cursor-not-allowed" : ""
                                  }`}
                                min={0}
                                disabled={!isAdmin}
                                title={
                                  !isAdmin ? "必要コマ数は管理者のみ編集できます" : undefined
                                }
                                readOnly={!isAdmin}
                              />
                            ) : (
                              c.required_slots
                            )}
                          </td>

                          {/* 消化コマ数 */}
                          <td className="p-2 border text-center font-semibold">
                            {isEditing ? (
                              <input
                                type="number"
                                name="consumed_slots"
                                value={editForm.consumed_slots}
                                onChange={handleEditChange}
                                onFocus={handleSelectAll}
                                className="border rounded p-1 w-full"
                                min={0}
                              />
                            ) : (
                              <span
                                className={
                                  fullyDone
                                    ? "text-green-700"
                                    : isOverdue
                                      ? "text-red-600"
                                      : "text-gray-800"
                                }
                              >
                                {c.consumed_slots}
                              </span>
                            )}
                          </td>

                          {/* 期限：オプション表示 */}
                          {showDueDate && (
                            <td className="p-2 border text-center whitespace-nowrap">
                              {isEditing ? (
                                <input
                                  type="date"
                                  name="due_date"
                                  value={editForm.due_date}
                                  onChange={handleEditChange}
                                  className="border rounded p-1 w-full"
                                />
                              ) : c.due_date ? (
                                formatDate(c.due_date)
                              ) : (
                                "-"
                              )}
                            </td>
                          )}

                          {/* 操作 */}
                          <td className="p-2 border text-center">
                            {isEditing ? (
                              <div className="flex gap-2 justify-center">
                                <button
                                  onClick={() => handleUpdate(c.id)}
                                  className="text-green-600 hover:text-green-800"
                                >
                                  <FaSave />
                                </button>
                                <button
                                  onClick={handleCancelEdit}
                                  className="text-gray-500 hover:text-gray-700"
                                >
                                  <FaTimes />
                                </button>
                              </div>
                            ) : (
                              <div className="flex gap-2 justify-center">
                                <button
                                  onClick={() => handleEdit(c)}
                                  className="text-blue-600 hover:text-blue-800"
                                >
                                  <FaEdit />
                                </button>
                                {isAdmin && (
                                  <button
                                    onClick={() => handleDelete(c.id)}
                                    className="text-red-600 hover:text-red-800"
                                  >
                                    <FaTrash />
                                  </button>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      ) : (
        <p className="text-gray-500">生徒を選択してください</p>
      )}


      {isModalOpen && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
          <div className="bg-white rounded shadow-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">カリキュラムを追加</h2>
              <button onClick={closeModal} className="text-gray-500 hover:text-gray-700">
                <FaTimes />
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-sm block mb-1">科目</label>
                <select
                  name="subjectid"
                  value={modalForm.subjectid}
                  onChange={handleModalChange}
                  className="border rounded p-2 w-full"
                >
                  <option value="">選択</option>
                  {subjects.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm block mb-1">単元名</label>
                <input
                  name="unit_name"
                  value={modalForm.unit_name}
                  onChange={handleModalChange}
                  className="border rounded p-2 w-full"
                />
              </div>
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="text-sm block mb-1">必要コマ数</label>
                  <input
                    type="number"
                    name="required_slots"
                    value={modalForm.required_slots}
                    onChange={handleModalChange}
                    className="border rounded p-2 w-full"
                    min={0}
                  />
                </div>
                <div className="flex-1">
                  <label className="text-sm block mb-1">消化コマ数</label>
                  <input
                    type="number"
                    name="consumed_slots"
                    value={modalForm.consumed_slots}
                    onChange={handleModalChange}
                    className="border rounded p-2 w-full"
                    min={0}
                  />
                </div>
              </div>
              <div>
                <label className="text-sm block mb-1">期限</label>
                <input
                  type="date"
                  name="due_date"
                  value={modalForm.due_date}
                  onChange={handleModalChange}
                  className="border rounded p-2 w-full"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={closeModal}
                className="px-4 py-2 rounded border border-gray-300 text-gray-600"
              >
                キャンセル
              </button>
              <button
                onClick={handleCreate}
                className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
              >
                追加する
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 授業報告書ページへ移動する固定ボタン */}
      {!isAdmin && (
        <button
          onClick={() => {
            window.location.href = "/report";
          }}
          className="
    fixed 
    bottom-6 
    right-6 
    bg-blue-600 
    text-white 
    shadow-lg 
    rounded-full 
    w-14 
    h-14 
    flex 
    items-center 
    justify-center 
    text-xl 
    hover:bg-blue-700
    active:scale-95
  "
          title="授業報告書ページへ"
        >
          📝
        </button>
      )}

    </div>
  );
};

export default StudentCurriculums;
